import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import TrashBin from '@components/icons/TrashBin.svg';
import { useState } from 'react';
import { TProps } from './types/TProps';

export const FeatureFlagsSection = ({
  organizationFlags,
  defaultFlags,
  isLoading,
  isError,
  isSaving,
  onSetFlag,
  onRemoveFlag,
  onRetry,
}: TProps) => {
  const [open, setOpen] = useState(true);
  const overriddenNames = Object.keys(organizationFlags).sort();
  const inheritedNames = Object.keys(defaultFlags)
    .filter((name) => !(name in organizationFlags))
    .sort();
  const title = `Feature Flags${overriddenNames.length ? ` (${overriddenNames.length} overridden)` : ''}`;

  return (
    <div className="flex w-full [&>div]:w-full">
      <DisclosureSection title={title} open={open} setOpenState={setOpen} loading={isLoading}>
        {isError ? (
          <div className="flex items-center gap-3 py-2 text-xs text-[var(--color-text-muted)]">
            <span>Failed to load the feature flags for this organization.</span>
            <TextButton type="button" onClick={onRetry}>
              Retry
            </TextButton>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 py-2 md:grid-cols-2">
            <div>
              <div className="pb-3 text-xs font-semibold text-[var(--color-text-muted)]">Organization overrides</div>
              {overriddenNames.length ? (
                <ul className="flex flex-col gap-3">
                  {overriddenNames.map((name) => (
                    <li key={name} className="flex items-center gap-3">
                      <Switch
                        label={name}
                        checked={organizationFlags[name]}
                        disabled={isSaving}
                        onChange={() => onSetFlag(name, !organizationFlags[name])}
                      />
                      <IconButtonDelete
                        title="Remove override and inherit the default"
                        aria-label={`Remove ${name} override`}
                        disabled={isSaving}
                        onClick={() => onRemoveFlag(name)}
                      >
                        <TrashBin />
                      </IconButtonDelete>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-[var(--color-text-muted)]">
                  No overrides. This organization uses the default value of every flag.
                </p>
              )}
            </div>
            <div>
              <div className="pb-3 text-xs font-semibold text-[var(--color-text-muted)]">
                Inherited defaults (toggle to override)
              </div>
              {inheritedNames.length ? (
                <ul className="flex flex-col gap-3">
                  {inheritedNames.map((name) => (
                    <li key={name} className="flex items-center gap-3">
                      <Switch
                        label={name}
                        checked={defaultFlags[name]}
                        disabled={isSaving}
                        onChange={() => onSetFlag(name, !defaultFlags[name])}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-[var(--color-text-muted)]">
                  Every flag is overridden for this organization.
                </p>
              )}
            </div>
          </div>
        )}
      </DisclosureSection>
    </div>
  );
};
